/**
 * UI-5a Task 5 — RecordingsPage.
 *
 * The recordings library: a filter sidebar + a virtualized recordings table +
 * a detail drawer + bulk delete.
 *
 * Composition (plan §5):
 *   - `useRecordingsList(filters)` owns fetching + lazy pagination + the
 *     client-side filter pass; the page only owns the `filters` object.
 *   - `<FilterSidebar>` is a controlled panel — it never owns filter state.
 *   - The PrimeReact `<DataTable>` is virtualized: `virtualScrollerOptions`
 *     supplies `itemSize` + `lazy: true` + `onLazyLoad → loadNextPage()` so the
 *     next 50-row page is appended as the user scrolls.
 *   - Row click opens `<RecordingDetailDrawer>` for that recording.
 *   - Bulk delete (gated by `recordings:delete`): a selection column + a header
 *     "Delete (n)" button → confirmDialog → one `api.delete` per row → refetch.
 *
 * Agent / queue name resolution (plan §0.A / §0.D): the backend list returns
 * `agentId` / `queueId` but not names. We resolve them up-front by mapping over
 * `filtered` and attaching `agentName` / `queueName` from the `useAgents()` /
 * `useContactCenterQueues()` lookup maps. Phase-1 fallback: when the map has no entry (or
 * isn't cached yet) we show `id.slice(0, 8)`. Doing it here (rather than inside
 * a Column body) keeps the resolution in one place and makes the resolved
 * `Recording` the single value flowing into the table + the detail drawer.
 */
import { useMemo, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import {
  DataTable,
  type DataTableSelectionMultipleChangeEvent,
} from 'primereact/datatable'
import { Column } from 'primereact/column'
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog'

import PageHeader from '../../components/admin/PageHeader.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import FilterSidebar from '../../components/admin/recordings/FilterSidebar.js'
import RecordingMiniPlayer from '../../components/admin/recordings/RecordingMiniPlayer.js'
import { RecordingDetailDrawer } from '../../components/admin/recordings/RecordingDetailDrawer.js'
import {
  useRecordingsList,
  type Recording,
  type RecordingFilters,
} from '../../hooks/useRecordingsList.js'
import { useAgents } from '../../hooks/useAgents.js'
import { useContactCenterQueues } from '../../hooks/useContactCenterQueues.js'
import { useAuthStore } from '../../store/authStore.js'
import { useToast } from '../../hooks/useToast.js'
import { api } from '../../api/client.js'
import { formatDate } from '../../utils/formatDate.js'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats a whole-second duration as `m:ss` (or `h:mm:ss` for long calls).
 *
 * `Recording.durationSec` is in SECONDS — the shared `formatDuration` in
 * `utils/formatDate.ts` takes MILLISECONDS and produces a different (`"1m 5s"`)
 * format, so a local mm:ss helper is used here. This mirrors the `formatMmSs`
 * already in `RecordingDetailDrawer`; it is small enough that duplicating it is
 * cleaner than introducing a shared-util import dependency between the two.
 */
function formatMmSs(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec < 0) return '—'
  const sec = Math.floor(totalSec)
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  const ss = String(s).padStart(2, '0')
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`
  return `${m}:${ss}`
}

const EMPTY_FILTERS: RecordingFilters = {
  fromDate: null,
  toDate: null,
  agentId: null,
  queueId: null,
  durationMinSec: null,
  durationMaxSec: null,
  hasTranscript: null,
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function RecordingsPage(): JSX.Element {
  const toast = useToast()
  const [filters, setFilters] = useState<RecordingFilters>(EMPTY_FILTERS)
  const { filtered, loading, error, loadNextPage, refetch, hasMore } =
    useRecordingsList(filters)

  const { agents } = useAgents()
  const { items: queues } = useContactCenterQueues()

  // --- Drawer + selection state -------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selected, setSelected] = useState<Recording | null>(null)
  const [bulkSelected, setBulkSelected] = useState<Recording[]>([])

  const permissions = useAuthStore((s) => s.user?.permissions ?? [])
  const canDelete = permissions.includes('recordings:delete')

  // --- Agent / queue name resolution --------------------------------------
  // Resolve once, up-front: map over `filtered` and attach agentName/queueName
  // from the lookup maps. Fallback to an 8-char id slice when unresolved.
  const agentNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const a of agents) map.set(a.id, `Ext. ${a.extension}`)
    return map
  }, [agents])

  const queueNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const q of queues) map.set(q.id, q.name)
    return map
  }, [queues])

  const rows = useMemo<Recording[]>(
    () =>
      filtered.map((r) => ({
        ...r,
        agentName: r.agentId
          ? (agentNameById.get(r.agentId) ?? r.agentId.slice(0, 8))
          : '—',
        queueName: r.queueId
          ? (queueNameById.get(r.queueId) ?? r.queueId.slice(0, 8))
          : '—',
      })),
    [filtered, agentNameById, queueNameById],
  )

  // PrimeReact's DataTableProps is a discriminated union — supplying
  // `selectionMode` pulls in the row/cell-selection variant (which also
  // requires `cellSelection`). Bundling the selection props into one typed
  // object, only when `canDelete`, keeps the union resolution clean and the
  // selection UI fully absent without the permission.
  const selectionProps = canDelete
    ? {
        selectionMode: 'multiple' as const,
        cellSelection: false as const,
        selection: bulkSelected,
        onSelectionChange: (
          e: DataTableSelectionMultipleChangeEvent<Recording[]>,
        ): void => setBulkSelected(e.value),
      }
    : {}

  // --- Bulk delete ---------------------------------------------------------
  function handleBulkDelete(): void {
    const targets = bulkSelected
    if (targets.length === 0) return
    confirmDialog({
      message: `Permanently delete ${targets.length} recording${
        targets.length === 1 ? '' : 's'
      }? This cannot be undone.`,
      header: 'Delete Recordings',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      // Each delete is individually try/catch-wrapped so one failure neither
      // aborts the batch nor escapes as an unhandled rejection. Any failures
      // are surfaced via a single error toast after the batch completes.
      accept: async () => {
        let failures = 0
        for (const r of targets) {
          try {
            await api.delete(`/recordings/${r.id}`)
          } catch {
            failures += 1
          }
        }
        if (failures > 0) {
          toast.error(
            `Failed to delete ${failures} of ${targets.length} recording${
              targets.length === 1 ? '' : 's'
            }.`,
          )
        } else {
          toast.success(
            `Deleted ${targets.length} recording${
              targets.length === 1 ? '' : 's'
            }.`,
          )
        }
        setBulkSelected([])
        await refetch()
      },
    })
  }

  return (
    <RequirePermission permission="admin:recordings:view">
      <div className="flex h-full">
        <FilterSidebar value={filters} onChange={setFilters} />

        <div className="flex-1 p-4">
          <PageHeader
            title="Recordings"
            description="Browse, play, and manage call recordings."
            actions={
              canDelete && bulkSelected.length > 0 ? (
                <Button
                  label={`Delete (${bulkSelected.length})`}
                  severity="danger"
                  onClick={handleBulkDelete}
                />
              ) : null
            }
          />

          {error !== null && (
            <div
              role="alert"
              className="mb-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <DataTable
            value={rows}
            dataKey="id"
            scrollable
            scrollHeight="flex"
            loading={loading}
            virtualScrollerOptions={{
              itemSize: 52,
              lazy: true,
              onLazyLoad: () => {
                if (hasMore) void loadNextPage()
              },
            }}
            {...selectionProps}
            onRowClick={(e) => {
              setSelected(e.data as Recording)
              setDrawerOpen(true)
            }}
            emptyMessage="No recordings."
          >
            {canDelete ? (
              <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
            ) : (
              <></>
            )}
            <Column
              field="createdAt"
              header="Date"
              body={(r: Recording) => formatDate(r.createdAt)}
            />
            <Column
              field="agentName"
              header="Agent"
              body={(r: Recording) => r.agentName ?? '—'}
            />
            <Column
              field="queueName"
              header="Queue"
              body={(r: Recording) => r.queueName ?? '—'}
            />
            <Column
              field="durationSec"
              header="Duration"
              body={(r: Recording) => formatMmSs(r.durationSec)}
            />
            <Column
              field="hasTranscript"
              header="Transcript"
              body={(r: Recording) => (r.hasTranscript ? '✓' : '—')}
            />
            <Column
              header="Play"
              body={(r: Recording) => (
                // Stop click propagation: a click on the mini player (play/
                // pause, scrubber) must NOT bubble to the row's `onRowClick`
                // and open the detail drawer.
                <div onClick={(e) => e.stopPropagation()}>
                  <RecordingMiniPlayer
                    recordingId={r.id}
                    durationSec={r.durationSec}
                  />
                </div>
              )}
            />
          </DataTable>

          <RecordingDetailDrawer
            open={drawerOpen}
            recording={selected}
            onClose={() => setDrawerOpen(false)}
            onDeleted={refetch}
          />
          <ConfirmDialog />
        </div>
      </div>
    </RequirePermission>
  )
}
